import {
  createJSONStorage,
  devtools,
  persist,
  subscribeWithSelector,
} from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createStore } from 'zustand/vanilla';
import type { RequestState } from '../@types/request-state.types';
import type { AuthMethod } from '../auth.types';
import type { AuthStore, AuthStoreState, UserData } from './auth-store.types';

const initialRequestState: RequestState = {
  data: null,
  status: 'idle',
  error: null,
};

const defaultInitialState: AuthStoreState = {
  token: null,
  params: null,
  user: {
    customerId: null,
    customer: { ...initialRequestState },
    personId: null,
    person: { ...initialRequestState },
  },
  isAuthenticating: false,
  isAuthenticated: false,
  authMethod: null,
  isInternal: false,
  redirectAfterAuthPath: null,
};

const name = '@axo/auth';

export const createAuthStore = (
  initialState: AuthStoreState = defaultInitialState
) =>
  createStore<AuthStore>()(
    subscribeWithSelector(
      devtools(
        persist(
          (set, get) => ({
            ...initialState,

            setToken: (token: string | null) => {
              if (!shallow(get().token, token))
                set(
                  {
                    token,
                    isAuthenticated: token !== null,
                    isAuthenticating: false,
                    authMethod: get().authMethod,
                  },
                  undefined,
                  'setToken'
                );
            },

            setCustomerId: (customerId: UserData['customerId']) => {
              if (!shallow(get().user.customerId, customerId))
                set(
                  (existingState) => ({
                    user: {
                      ...existingState.user,
                      customerId,
                    },
                  }),
                  undefined,
                  'setCustomerId'
                );
            },

            setPersonId: (personId: UserData['personId']) => {
              if (!shallow(get().user.personId, personId))
                set(
                  (existingState) => ({
                    user: {
                      ...existingState.user,
                      personId,
                    },
                  }),
                  undefined,
                  'setPersonId'
                );
            },

            setRequestState: (
              key: keyof UserData,
              state: Partial<RequestState>
            ) =>
              set(
                (existingState) => ({
                  user: {
                    ...existingState.user,
                    [key]: {
                      ...(existingState.user[key] as RequestState),
                      ...state,
                    },
                  },
                }),
                undefined,
                `set${key.charAt(0).toUpperCase()}${key.slice(1)}`
              ),

            setParams: (params: AuthStoreState['params']) =>
              set({ params }, undefined, 'setParams'),

            setIsAuthenticating: (
              isAuthenticating: boolean,
              authMethod?: AuthMethod | null
            ) =>
              set(
                { isAuthenticating, authMethod },
                undefined,
                'setIsAuthenticating'
              ),

            setIsInternal: (isInternal: boolean) =>
              set({ isInternal }, undefined, 'setIsInternal'),

            setRedirectAfterAuthPath: (redirectAfterAuthPath: string | null) =>
              set(
                { redirectAfterAuthPath },
                undefined,
                'setRedirectAfterAuthPath'
              ),

            reset: () => set(defaultInitialState),
          }),
          {
            name,
            storage: createJSONStorage(() => sessionStorage),
            version: 1,
            partialize: (state) => {
              // exclude non-essential state (back-end data, ...) from being persisted
              const {
                user,
                params,
                // isAuthenticated,
                isAuthenticating,
                ...rest
              } = state;
              const { customer, ...userRest } = user;
              return { ...rest, user: { ...userRest } };
            },
          }
        ),
        { name }
      )
    )
  );

export const _authStore = createAuthStore();
